import {
  Add,
  AdsClick,
  ArrowBack,
  ArrowDropDown,
  AutoAwesome,
  BarChart,
  BookmarkBorder,
  BrokenImage,
  CopyAll,
  DeleteOutline,
  DeleteSweep,
  Download,
  ElectricBolt,
  GraphicEq,
  Groups,
  Http,
  InfoOutlined,
  LinkedIn,
  LocalOffer,
  MoreVert,
  MoveDown,
  PersonSearch,
  Place,
  PlayCircle,
  PlaylistAdd,
  Refresh,
  Reorder,
  Search,
  SettingsApplications,
  Stop,
  Sync,
  TextFields,
  Timeline,
  TrackChanges,
  UploadFile,
  UploadFileOutlined,
  Visibility,
  VisibilityOff,
  VolumeOff,
  Webhook,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridColumnMenuColumnsItem,
  GridColumnMenuFilterItem,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import Stack from "@mui/material/Stack";
import { Bot, ChevronsLeftRight, Image as LucideImage } from "lucide-react";
import { SvgIcon } from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";

// Component to show long text with popover on hover and edit dialog on click
// Does NOT auto-truncate text - lets CSS handle natural overflow with ellipsis
// Only enables popover/click-to-edit for text longer than the threshold
const LongTextCell: React.FC<{
  value: string;
  maxLength?: number;
  onSave?: (newValue: string) => Promise<void>;
  editable?: boolean;
}> = ({ value, maxLength = 50, onSave, editable = true }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isLongText = value && value.length > maxLength;

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isLongText && !dialogOpen) {
        setAnchorEl(event.currentTarget);
      }
    },
    [isLongText, dialogOpen]
  );

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback((event: React.MouseEvent) => {
    if (isLongText) {
      // Stop propagation to prevent DataGrid from entering edit mode
      event.stopPropagation();
      setAnchorEl(null);
      setDialogOpen(true);
    }
  }, [isLongText]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSave = useCallback(
    async (newValue: string) => {
      if (onSave) {
        await onSave(newValue);
      }
      setDialogOpen(false);
    },
    [onSave]
  );

  const open = Boolean(anchorEl);

  if (!value) return null;

  return (
    <>
      <Box
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        onClick={handleClick}
        sx={{
          cursor: isLongText ? "pointer" : "default",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {value}
      </Box>
      {isLongText && (
        <Popover
          sx={{
            pointerEvents: "none",
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Box
            sx={{
              p: 2,
              maxWidth: 400,
              maxHeight: 300,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <Typography variant="body2">{value}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Click to {editable ? "edit" : "view full text"}
            </Typography>
          </Box>
        </Popover>
      )}
      <PopOutTextDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        value={value}
        title={editable ? "Edit Text" : "View Text"}
      />
    </>
  );
};
import { useNavigate } from "react-router-dom";
import LoadingButton from "../../components/LoadingButton";
import LoadingIconButton from "../../components/LoadingIconButton";
import PopOutTextDialog from "../../components/PopOutTextDialog";
import {
  LIST_ACTION_TYPE_AUDIENCELAB_ENRICH,
  LIST_ACTION_TYPE_UPDATE_ROWS,
  LIST_ACTIONS,
  LIST_HEADER_TYPE_LOOKUP,
  ListAction,
  LOOKUP_TYPE_SEQUENCE_STATUS,
} from "../../constants";
import { FEATURES } from "../../constants/features";
import { INTEGRATION_LOGOS } from "../integrations/constants";
import { useFeatures } from "../../context/FeatureContext";
import { Company } from "../../types";
import { hostname, parseError, sanitizeFilename } from "../../utils";
import ActionTable from "./ActionTable";
import AddColumnDialog from "./AddColumnDialog";
import BackgroundTasksPanel from "./BackgroundTasksPanel";
import ImportModal from "./ImportModal";
import ListActionDialog from "./ListActionDialog";
import ListActionSummaryDialog from "./ListActionSummaryDialog";
import ListAgentRunSummaryDialog from "./ListAgentRunSummaryDialog";
import ListColumnTypeDialog, { TYPE_OPTIONS } from "./ListColumnTypeDialog";
import { getTagColor } from "./utils/tagColors";
import { StyledDataGrid } from "./StyledGrid";
import TemplateEditor from "./TemplateEditor";
import WebhookDialog from "./WebhookDialog";
import ActionMetricsDialog from "./dialogs/ActionMetricsDialog";
import ApifyLeadsFinderDialog from "./dialogs/ApifyLeadsFinderDialog";
import ApolloSearchDialog from "./dialogs/ApolloSearchDialog";
import GoogleMapsSearchDialog from "./dialogs/GoogleMapsSearchDialog";
import AudienceBuilderDialog from "./dialogs/AudienceBuilderDialog";
import ApolloIcon from "../../components/icons/ApolloIcon";
import AudienceLabIcon from "../../components/icons/AudienceLabIcon";
import ColumnOrderDialog from "./dialogs/ColumnOrderDialog";
import CompaniesSearchDialog from "./dialogs/CompaniesSearchDialog";
import DeduplicateDialog from "./dialogs/DeduplicateDialog";
import SavedViewDialog, { SavedView } from "./dialogs/SavedViewDialog";
import LinkedInPostImportDialog from "./dialogs/LinkedInPostImportDialog";
import MoveToListDialog from "./dialogs/MoveToListDialog";
import MoveToSequenceDialog from "./dialogs/MoveToSequenceDialog";
import PixelDialog from "./dialogs/PixelDialog";
import RowDetailDialog from "./dialogs/RowDetailDialog";
import {
  detectPiiType,
  getPiiObfuscationState,
  obfuscateValue,
  setPiiObfuscationState,
  shouldUseBlur,
} from "./utils/piiObfuscation";

// Wrapper to make Lucide Bot icon compatible with MUI
const BotIcon = (props: any) => (
  <SvgIcon {...props}>
    <Bot />
  </SvgIcon>
);

// Wrapper to make Lucide Image icon compatible with MUI
const ImageIcon = (props: any) => (
  <SvgIcon {...props}>
    <LucideImage />
  </SvgIcon>
);

// MUI icon mapping for list actions
const MUI_ICONS: Record<string, React.ElementType> = {
  SmartToy: BotIcon,
  Timeline,
  Http,
  PlaylistAdd,
  AutoAwesome,
  Image: ImageIcon,
  GraphicEq,
  Search,
  Sync,
  AdsClick,
  BarChart,
  LocalOffer,
};

// Helper component to render action icon (MUI icon or integration logo)
const ActionIcon: React.FC<{ action: ListAction }> = ({ action }) => {
  if (action.icon.type === "logo") {
    const logoSrc = INTEGRATION_LOGOS[action.icon.name];
    if (logoSrc) {
      return (
        <Avatar
          src={logoSrc}
          alt={action.title}
          sx={{ width: 24, height: 24 }}
        />
      );
    }
  }

  // MUI icon
  const IconComponent = MUI_ICONS[action.icon.name];
  if (IconComponent) {
    return <IconComponent sx={{ fontSize: 24, color: "text.secondary" }} />;
  }

  return null;
};

export default ({
  mode,
  currentUser,
  getToken,
  eventListener,
  companyId,
  company,
  currentPlan,
  instanceId,
  openDialog,
}: {
  mode: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
  instanceId: string | undefined | null;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
}) => {
  const navigate = useNavigate();
  const { isFeatureLocked, isFeatureHidden, isFeatureEnabled } = useFeatures();

  // Filter LIST_ACTIONS based on feature flag status
  // Audience enrichment is only available when the feature flag is enabled
  const availableListActions = React.useMemo(() => {
    return LIST_ACTIONS.filter((action) => {
      if (action.type === LIST_ACTION_TYPE_AUDIENCELAB_ENRICH) {
        return isFeatureEnabled(FEATURES.AUDIENCE_BUILDER);
      }
      return true;
    });
  }, [isFeatureEnabled]);

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [rawHeaders, setRawHeaders] = useState<Array<any>>([]);
  const [rows, setRows] = useState<Array<any>>([]);
  const [totalRows, setTotalRows] = useState(100);
  const [actions, setActions] = useState<Array<any>>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [actionOptions, setActionOptions] = useState<any | null>(null);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = React.useState<any[]>([]);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [actionAnchor, setActionAnchor] = useState<any>(null);
  const [actionAnchorPosition, setActionAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [importEl, setImportEl] = useState<any>(null);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [listActionTitle, setListActionTitle] = useState("");
  const [listActionType, setListActionType] = useState("");
  const paginationRef = useRef(paginationModel);
  const [editHeaderOpen, setEditHeaderOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState<any>(null);
  const [agentRunSummaryOpen, setAgentRunSummaryOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [rowDetailOpen, setRowDetailOpen] = useState(false);
  const [rowDetailRow, setRowDetailRow] = useState<any>(null);
  const [actionSummaryOpen, setActionSummaryOpen] = useState(false);
  const [selectedActionForSummary, setSelectedActionForSummary] =
    useState<any>(null);
  const [actionMetricsOpen, setActionMetricsOpen] = useState(false);
  const [selectedActionForMetrics, setSelectedActionForMetrics] =
    useState<any>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<any>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<any>(null);
  const [companiesSearchOpen, setCompaniesSearchOpen] = useState(false);
  const [linkedInPostImportOpen, setLinkedInPostImportOpen] = useState(false);
  const [apifyLeadsFinderOpen, setApifyLeadsFinderOpen] = useState(false);
  const [apolloSearchOpen, setApolloSearchOpen] = useState(false);
  const [googleMapsSearchOpen, setGoogleMapsSearchOpen] = useState(false);
  const [hasApolloIntegration, setHasApolloIntegration] = useState(false);
  const [audienceLabDialogOpen, setAudienceLabDialogOpen] = useState(false);
  const [moveToSequenceOpen, setMoveToSequenceOpen] = useState(false);
  const [moveToListOpen, setMoveToListOpen] = useState(false);
  const [deduplicateOpen, setDeduplicateOpen] = useState(false);
  const [pixelDialogOpen, setPixelDialogOpen] = useState(false);
  const [columnOrderDialogOpen, setColumnOrderDialogOpen] = useState(false);
  const [savedViewDialogOpen, setSavedViewDialogOpen] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const isSearchTriggeredRef = useRef(false);
  const [backgroundTaskRefreshTrigger, setBackgroundTaskRefreshTrigger] =
    useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const [piiObfuscationEnabled, setPiiObfuscationEnabled] = useState(() =>
    getPiiObfuscationState()
  );

  // Toggle PII obfuscation and persist to localStorage
  const togglePiiObfuscation = useCallback(() => {
    setPiiObfuscationEnabled((prev) => {
      const newValue = !prev;
      setPiiObfuscationState(newValue);
      return newValue;
    });
  }, []);

  const originalPoll = (fn: any, end: any, tries: number) => {
    async function checkCondition(curr: number) {
      const stillLoadingList = await fn();
      console.log("result", stillLoadingList);
      if (curr < tries && stillLoadingList && stillLoadingList.length > 0) {
        setTimeout(checkCondition, 3500, curr + 1);
      } else {
        end();
      }
    }
    checkCondition(1);
  };

  const runTableAction = async (
    actionId: any,
    type: string,
    selectedRows?: GridRowSelectionModel,
    forceRun: boolean = false
  ) => {
    console.log("Running table action", actionId, type);
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/lists/${instanceId}/action/run`,
        {
          companyId,
          actionId: actionId,
          type: type,
          selectedRows: selectedRows,
          forceRun,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        originalPoll(
          silentGetList,
          () => console.log("completed polling"),
          100
        );
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const stopTableAction = async (actionId: any, actionType: string) => {
    console.log("Running table action", actionId, actionType);
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/lists/${instanceId}/action/stop`,
        {
          companyId,
          actionId,
          actionType,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        originalPoll(silentGetList, () => console.log("completed polling"), 50);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const addLookupColumn = async (lookupType: string) => {
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/lists/${instanceId}/lookup`,
        { companyId, lookupType },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      )
      .then(() => {
        silentGetList();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const deleteTableAction = async (action: any) => {
    console.log("Deleting table action", action.id, action.type);
    openDialog(
      "Delete Action",
      `Are you sure you want to delete the action "${action.name}"? This cannot be undone.`,
      "Confirm",
      async () => {
        const currentToken = await getToken();
        await axios
          .delete(
            `${hostname}/lists/${instanceId}/action/${action.id}?companyId=${companyId}&actionType=${action.type}`,
            {
              headers: { Authorization: `Bearer ${currentToken}` },
            }
          )
          .then((res) => {
            originalPoll(silentGetList, () => console.log("completed polling"), 50);
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          });
      }
    );
  };

  const exportList = async () => {
    setExportLoading(true);
    try {
      const currentToken = await getToken();
      const params = new URLSearchParams({
        companyId: companyId,
      });

      // Include current sort settings
      if (sortModel.length > 0) {
        params.append("sortField", sortModel[0].field);
        params.append("sortOrder", sortModel[0].sort || "asc");
      }

      // Include search settings
      if (searchQuery) {
        params.append("searchQuery", searchQuery);
        params.append("searchField", searchField);
      }

      // Include filter model if any filters are applied
      if (filterModel.items && filterModel.items.length > 0) {
        params.append("filterModel", JSON.stringify(filterModel));
      }

      const response = await fetch(
        `${hostname}/lists/${instanceId}/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      // Handle background export for large lists (202 response)
      if (response.status === 202) {
        const data = await response.json();
        eventListener({
          type: "SET_SUCCESS",
          message: data.message,
        });
        return;
      }

      if (!response.ok) {
        throw new Error("Export failed");
      }

      // Get the CSV blob
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sanitizeFilename(name || "list", "list")}_export.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setExportLoading(false);
    }
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
          <LoadingButton
            size="small"
            startIcon={<Download />}
            onClick={exportList}
            loading={exportLoading}
          >
            Export All
          </LoadingButton>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={getList}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            size="small"
            startIcon={<BookmarkBorder />}
            onClick={() => setSavedViewDialogOpen(true)}
          >
            Views
          </Button>
          <Button
            size="small"
            startIcon={piiObfuscationEnabled ? <VisibilityOff /> : <Visibility />}
            onClick={togglePiiObfuscation}
          >
            Sensitive
          </Button>
        </Box>
      </GridToolbarContainer>
    );
  }

  const formatDateValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    const str = String(value);
    const date = new Date(str);
    if (isNaN(date.getTime())) return str;
    const hasTime = /[T\s]\d{2}:\d{2}/.test(str);
    return hasTime
      ? date.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const FieldTypeIcon = ({ type }: { type: string }) => {
    const typeObj = TYPE_OPTIONS.find((x) => x.value === type);
    if (typeObj) {
      return typeObj.icon;
    }
    return <TextFields />;
  };

  const handleApplySavedView = (view: SavedView) => {
    if (view.filterModel) {
      setFilterModel(view.filterModel);
    }
    if (view.sortModel) {
      setSortModel(view.sortModel);
    }
    if (view.columnVisibilityModel) {
      setColumnVisibilityModel(view.columnVisibilityModel);
    }
  };

  const saveRow = async (row: any) => {
    const currentToken = await getToken();
    return axios.put(
      `${hostname}/lists/${instanceId}/row`,
      { companyId, row },
      {
        headers: { Authorization: `Bearer ${currentToken}` },
      }
    );
  };

  // Helper function to update a cell value from the long text dialog
  const updateCellValue = async (rowId: string, field: string, newValue: string) => {
    // Use functional update to get current rows and avoid stale closure
    let updatedRow: any = null;

    setRows((prevRows) => {
      const rowIndex = prevRows.findIndex((r: any) => r.rowId === rowId);
      if (rowIndex === -1) return prevRows;

      updatedRow = { ...prevRows[rowIndex], [field]: newValue };
      const newRows = [...prevRows];
      newRows[rowIndex] = updatedRow;
      return newRows;
    });

    // Save to backend
    if (updatedRow) {
      await saveRow(updatedRow);
    }
  };

  const buildColumns = (headers: any[], includeDateAdded: boolean) => {
    const headersWithDateAdded = includeDateAdded
      ? [
          ...headers,
          {
            id: "__dateAdded",
            name: "Date Added",
            type: "text",
            dataType: "date",
            __systemColumn: true,
          },
        ]
      : headers;
    const builtHeaders = headersWithDateAdded.map((x: any) => {
      var field: any = {
        field: x.name,
        headerName: x.name,
        editable: true,
        width: 195,
      };
      if (x.type === LIST_HEADER_TYPE_LOOKUP) {
        field["sortable"] = false;
        field["editable"] = false;
        field["headerId"] = x.id;
        field["name"] = x.name;
        field["renderHeader"] = () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Timeline sx={{ fontSize: 18, color: "text.secondary" }} />
            <strong>{x.name}</strong>
          </Box>
        );
        field["renderCell"] = (params: GridCellParams) => {
          const cellValue = params.value as string | null;
          if (!cellValue) {
            return null;
          }
          const lookupType = x.actionParameters?.lookupType;
          if (lookupType === LOOKUP_TYPE_SEQUENCE_STATUS) {
            const colorByStatus: Record<
              string,
              "primary" | "default" | "warning" | "error"
            > = {
              Active: "primary",
              Completed: "default",
              Paused: "warning",
              Unsubscribed: "error",
              Bounced: "error",
              Removed: "error",
            };
            const chipColor = colorByStatus[cellValue] || "default";
            return (
              <Chip
                label={cellValue}
                color={chipColor}
                size="small"
                variant={chipColor === "default" ? "outlined" : "filled"}
              />
            );
          }
          return <>{cellValue}</>;
        };
      } else if (x.type === "action") {
        field["sortable"] = false;
        field["type"] = "action";
        field["editable"] = false;
        field["width"] = 230;
        field["headerId"] = x.id;
        field["loading"] = x.loading;
        field["name"] = x.name;
        field["actionParameters"] = x.actionParameters;
        field["autoRunEnabled"] = x.autoRunEnabled;
        field["conditions"] = x.conditions;
        field["renderHeader"] = (params: GridColumnHeaderParams) => {
          // Determine action type - check actionType first, then fall back to detecting by fields
          let actionType = x.actionParameters?.actionType;
          if (!actionType) {
            // Fallback detection for legacy actions without actionType
            if (x.actionParameters?.goal) actionType = "agent";
            else if (x.actionParameters?.sequenceId) actionType = "add_sequence";
            else if (x.actionParameters?.url && x.actionParameters?.method) actionType = "http_request";
            else if (x.actionParameters?.targetListId) actionType = "add_to_list";
            else if (x.actionParameters?.prompt && x.actionParameters?.model) actionType = "ai_text";
            else if (x.actionParameters?.task) actionType = "browser_task";
            else if (x.actionParameters?.crmIntegrationId) actionType = "crm_sync";
            else if (x.actionParameters?.apolloIntegrationId) actionType = "apollo_enrich";
            else if (x.actionParameters?.findymailIntegrationId) actionType = "findymail_enrich";
          }
          const actionDef = LIST_ACTIONS.find((a) => a.type === actionType);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {actionDef && <ActionIcon action={actionDef} />}
              <strong>{x.name}</strong>
              <LoadingIconButton
                loading={x.loading}
                size="small"
                onClick={(event: SyntheticEvent) => {
                  setActionAnchor(event.currentTarget);
                  setSelectedActionId(x.id);
                }}
              >
                <PlayCircle />
              </LoadingIconButton>
            </Box>
          );
        };
        field["renderCell"] = (params: GridCellParams) => {
          if (params.value == null) {
            return null;
          }

          // Handle AI image generation action type
          if (x.actionParameters.actionType === "ai_image") {
            // @ts-ignore
            const cellValue: string = params.value;
            // Check if the value looks like a URL (contains data: or http)
            if (
              cellValue &&
              (cellValue.startsWith("data:") || cellValue.startsWith("http"))
            ) {
              return (
                <Box
                  sx={{
                    height: 50,
                    width: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    height={50}
                    width={50}
                    style={{ objectFit: "cover" }}
                    src={/^https?:\/\/[a-zA-Z0-9]/.test(cellValue) ? cellValue : ""}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      // @ts-ignore
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                  <BrokenImage
                    sx={{
                      display: "none",
                      color: "text.disabled",
                      width: 40,
                      height: 40,
                    }}
                  />
                </Box>
              );
            } else {
              console.log("Non-URL value for AI image generation:", params);
              // Show text for non-URL values (like status messages)
              return <LongTextCell value={cellValue} editable={false} />;
            }
          }

          // Handle AI audio generation action type
          if (x.actionParameters.actionType === "ai_audio") {
            // @ts-ignore
            const cellValue: string = params.value;
            // Check if the value looks like a URL (contains data: or http)
            if (
              cellValue &&
              (cellValue.startsWith("data:") || cellValue.startsWith("http"))
            ) {
              return (
                <Box
                  sx={{
                    height: 50,
                    width: 200,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <audio
                    controls
                    style={{ height: 40, width: 180 }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      // @ts-ignore
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  >
                    <source src={cellValue} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                  <VolumeOff
                    sx={{
                      display: "none",
                      color: "text.disabled",
                      width: 40,
                      height: 40,
                    }}
                  />
                </Box>
              );
            } else {
              // Show text for non-URL values (like status messages)
              return <LongTextCell value={cellValue} editable={false} />;
            }
          }

          // Handle agent, browser_task, and http_request action types (all have researchSteps/researchSummary)
          if (
            x.actionParameters.actionType === "agent" ||
            x.actionParameters.actionType === "browser_task" ||
            x.actionParameters.actionType === "http_request" ||
            x.actionParameters.actionType === undefined
          ) {
            // @ts-ignore
            const cellValue: string = params.value;
            return (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  "&:hover .preview-button": {
                    visibility: "visible",
                    opacity: 1,
                  },
                  "&:hover .text-preview": {
                    visibility: "hidden",
                    opacity: 0,
                  },
                }}
              >
                <>{cellValue}</>
                <Button
                  className="preview-button"
                  variant="contained"
                  size="small"
                  sx={{
                    position: "absolute",
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                    left: 0,
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                  onClick={() => {
                    setAgentRunSummaryOpen(true);
                    setSelectedHeader(x);
                    setSelectedRowId(params.id.toString());
                  }}
                  startIcon={<Visibility />}
                >
                  Details
                </Button>
              </Box>
            );
          }

          // Handle other action types (ai_text, http_request, etc.)
          // @ts-ignore
          const otherCellValue: string = params.value;
          return <LongTextCell value={otherCellValue} editable={false} />;
        };
      } else {
        field["header"] = x;
        field["headerId"] = x.id;
        field["name"] = x.name;
        if (x.dataType === "tags") {
          field["editable"] = false;
        }
        field["renderHeader"] = (params: GridColumnHeaderParams) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FieldTypeIcon type={x.dataType} />
            <Box sx={{ ml: 1 }}>{x.name}</Box>
          </Box>
        );
        field["renderCell"] = (params: GridCellParams) => {
          if (params.value == null) {
            return null;
          }
          // @ts-ignore
          const cellValue: string = params.value;

          // Apply PII obfuscation if enabled
          const piiType = piiObfuscationEnabled
            ? detectPiiType(x.dataType || "", x.name || params.field)
            : null;
          const useBlur = piiType && shouldUseBlur(piiType);
          const displayValue = piiType
            ? obfuscateValue(cellValue, piiType)
            : cellValue;

          // Blur style for fully obfuscated fields
          const blurStyle = useBlur
            ? { filter: "blur(5px)", userSelect: "none" as const }
            : {};

          if (x.dataType === "email") {
            return (
              <Link
                href={piiObfuscationEnabled ? undefined : (/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+$/.test(cellValue) ? `mailto:${cellValue}` : "#")}
                sx={{
                  textDecoration: "none",
                  cursor: piiObfuscationEnabled ? "default" : "pointer",
                  ...blurStyle,
                }}
                onClick={piiObfuscationEnabled ? (e) => e.preventDefault() : undefined}
              >
                {displayValue}
              </Link>
            );
          } else if (x.dataType === "phone") {
            return (
              <Link
                href={piiObfuscationEnabled ? undefined : (/^[0-9+\-().\s]+$/.test(cellValue) ? `tel:${cellValue}` : "#")}
                sx={{
                  textDecoration: "none",
                  cursor: piiObfuscationEnabled ? "default" : "pointer",
                  ...blurStyle,
                }}
                onClick={piiObfuscationEnabled ? (e) => e.preventDefault() : undefined}
              >
                {displayValue}
              </Link>
            );
          } else if (x.dataType === "linkedin_url") {
            return (
              <Link
                href={piiObfuscationEnabled ? undefined : (/^https?:\/\/[a-zA-Z0-9]/.test(cellValue) ? cellValue : "#")}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  cursor: piiObfuscationEnabled ? "default" : "pointer",
                  ...blurStyle,
                }}
                onClick={piiObfuscationEnabled ? (e) => e.preventDefault() : undefined}
              >
                {displayValue}
              </Link>
            );
          } else if (x.dataType === "date") {
            return <>{formatDateValue(cellValue)}</>;
          } else if (x.dataType === "text") {
            return (
              <Box sx={blurStyle}>
                <LongTextCell
                  value={displayValue}
                  onSave={piiObfuscationEnabled ? undefined : async (newValue) => {
                    await updateCellValue(params.id.toString(), params.field, newValue);
                  }}
                  editable={!piiObfuscationEnabled}
                />
              </Box>
            );
          } else if (x.dataType === "url") {
            return (
              <Link href={/^https?:\/\/[a-zA-Z0-9]/.test(cellValue) ? cellValue : "#"} target="_blank" rel="noopener noreferrer">
                {cellValue}
              </Link>
            );
          } else if (x.dataType === "tags") {
            let tags: string[] = [];
            try {
              const parsed = JSON.parse(cellValue);
              if (Array.isArray(parsed)) {
                tags = parsed.filter((t) => typeof t === "string" && t);
              }
            } catch {
              // Tolerate legacy comma-separated values
              tags = cellValue
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
            }
            if (!tags.length) return null;
            return (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                {tags.map((t) => {
                  const color = getTagColor(t);
                  return (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      sx={{
                        backgroundColor: color.bg,
                        color: color.text,
                        border: `1px solid ${color.border}`,
                        fontWeight: 500,
                      }}
                    />
                  );
                })}
              </Box>
            );
          } else if (x.dataType === "image") {
            return (
              <Box
                sx={{
                  height: 50,
                  width: 50,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  height={50}
                  width={50}
                  style={{ objectFit: "cover" }}
                  src={/^https?:\/\/[a-zA-Z0-9]/.test(cellValue) ? cellValue : ""}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    // @ts-ignore
                    e.currentTarget.nextSibling.style.display = "flex";
                  }}
                />
                <BrokenImage
                  sx={{
                    display: "none",
                    color: "text.disabled",
                    width: 40,
                    height: 40,
                  }}
                />
              </Box>
            );
          }

          // Default fallback for other types - check if PII by column name
          return (
            <Box sx={blurStyle}>
              <LongTextCell
                value={displayValue}
                onSave={piiObfuscationEnabled ? undefined : async (newValue) => {
                  await updateCellValue(params.id.toString(), params.field, newValue);
                }}
                editable={!piiObfuscationEnabled && !piiType}
              />
            </Box>
          );
        };
      }
      if (x.__systemColumn) {
        field["field"] = "dateAdded";
        field["headerName"] = "Date Added";
        field["editable"] = false;
        field["sortable"] = false;
        field["width"] = 180;
        field["__systemColumn"] = true;
        field["disableColumnMenu"] = true;
        delete field["headerId"];
        delete field["header"];
      }
      return field;
    });
    const detailColumn: any = {
      field: "__rowDetail",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderHeader: () => (
        <Tooltip title="Open row details to view, edit, and see change history" arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              color: "text.secondary",
            }}
          >
            <InfoOutlined fontSize="small" />
          </Box>
        </Tooltip>
      ),
      renderCell: (params: GridCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setRowDetailRow(params.row);
            setRowDetailOpen(true);
          }}
        >
          <ChevronsLeftRight size={18} />
        </IconButton>
      ),
    };
    return [detailColumn, ...builtHeaders];
  };

  // Generate columns from rawHeaders, re-render when piiObfuscationEnabled changes
  const columns = React.useMemo(() => {
    const hasData = !loading && rows.length > 0 && rawHeaders.length > 0;
    return buildColumns(rawHeaders, hasData);
  }, [rawHeaders, piiObfuscationEnabled, loading, rows.length]);

  // Data columns only — excludes DataGrid-only synthetic columns (__rowDetail,
  // __systemColumn / Date Added). Always pass THIS to dialogs/menus that
  // operate on real list columns. Only the DataGrid itself should use `columns`.
  const dataColumns = React.useMemo(
    () =>
      columns.filter(
        (col: any) => col.field !== "__rowDetail" && !col.__systemColumn
      ),
    [columns]
  );

  const addRow = async () => {
    const currentToken = await getToken();
    return axios
      .post(
        `${hostname}/lists/${instanceId}/row`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res: any) => {
        if (res.data.row) {
          // Create a new row object with all column fields initialized
          const newRow: any = { rowId: res.data.row.id };

          // Initialize all column fields to null/empty
          dataColumns.forEach((col: any) => {
            if (col.field !== "rowId") {
              newRow[col.field] = null;
            }
          });

          setRows((oldRows) => [newRow, ...oldRows]);
        }
      });
  };

  const addColumn = async (name: string) => {
    setAddingColumn(true);
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/lists/${instanceId}/header`,
        { companyId, name },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      setAddColumnOpen(false);
      getList();
    } catch (err: any) {
      eventListener({ type: "SET_ERROR", error: parseError(err) });
    } finally {
      setAddingColumn(false);
    }
  };

  const isAnyHeaderLoading = (res: any) => {
    return res.data.list.headers.filter((x: any) => x.loading);
  };

  const isAnyActionsLoading = (res: any) => {
    return res.data.list.actions.filter((x: any) => x.loading);
  };

  const silentGetList = async () => {
    const currentToken = await getToken();
    var tmpPgm = { page: 1, pageSize: 50 };
    var tmpSort = [] as any[];
    setPaginationModel((pgm) => {
      tmpPgm = pgm;
      return pgm;
    });
    setSortModel((sm) => {
      tmpSort = sm;
      return sm;
    });

    // Prepare sorting parameters
    const sortField = tmpSort.length > 0 ? tmpSort[0].field : undefined;
    const sortOrder = tmpSort.length > 0 ? tmpSort[0].sort : undefined;

    return axios
      .get(`${hostname}/lists/${instanceId}`, {
        params: {
          companyId,
          page: tmpPgm.page + 1,
          pageSize: tmpPgm.pageSize,
          sortField,
          sortOrder,
          searchQuery,
          searchField,
        },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setRawHeaders(res.data.list.headers);
        setRows(res.data.list.rows.items);
        setTotalRows(res.data.list.rows.total);
        setActions(res.data.list.actions);
        return isAnyHeaderLoading(res) + isAnyActionsLoading(res);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  function CustomColumnMenu(props: GridColumnMenuProps) {
    const { colDef, hideMenu } = props;
    const isActionColumn = (colDef as any).type === "action";
    const isLoading = (colDef as any).loading;

    const itemProps = {
      colDef: colDef,
      onClick: hideMenu,
    };

    if (isActionColumn) {
      // Action column menu
      return (
        <React.Fragment>
          {/* Action execution controls - at top */}
          <Stack px={0.5} py={0.5}>
            {isLoading && (
              <MenuItem
                onClick={(event) => {
                  stopTableAction(
                    (colDef as any).headerId,
                    LIST_ACTION_TYPE_UPDATE_ROWS
                  );
                  hideMenu(event);
                }}
              >
                <ListItemIcon>
                  <Stop fontSize="small" />
                </ListItemIcon>
                <ListItemText>Stop Action</ListItemText>
              </MenuItem>
            )}
            {isLoading && (
              <MenuItem
                onClick={(event) => {
                  setSelectedActionForMetrics({
                    id: (colDef as any).headerId,
                    type: LIST_ACTION_TYPE_UPDATE_ROWS,
                    name: (colDef as any).name,
                  });
                  setActionMetricsOpen(true);
                  hideMenu(event);
                }}
              >
                <ListItemIcon>
                  <BarChart fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Progress</ListItemText>
              </MenuItem>
            )}
            {!isLoading && (
              <MenuItem
                onClick={(event: React.MouseEvent) => {
                  setActionAnchorPosition({
                    top: event.clientY,
                    left: event.clientX,
                  });
                  setSelectedActionId((colDef as any).headerId);
                  hideMenu(event);
                }}
              >
                <ListItemIcon>
                  <PlayCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>Run Action</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={(event) => {
                setResearchOpen(true);
                setActionOptions({
                  actionId: (colDef as any).headerId,
                  conditions: (colDef as any).conditions,
                  autoRunEnabled: (colDef as any).autoRunEnabled,
                  actionParameters: {
                    name: (colDef as any).name,
                    ...(colDef as any).actionParameters,
                  },
                });
                const actionType =
                  (colDef as any).actionParameters?.actionType || "agent";
                const listAction = LIST_ACTIONS.find(
                  (x) => x.type === actionType
                );
                setListActionType(listAction?.type ?? LIST_ACTIONS[0].type);
                setListActionTitle(listAction?.title ?? LIST_ACTIONS[0].title);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <SettingsApplications fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Action</ListItemText>
            </MenuItem>
          </Stack>
          <Divider />
          {/* Sorting and filtering */}
          <Stack px={0.5} py={0.5}>
            <GridColumnMenuSortItem {...itemProps} />
            <GridColumnMenuFilterItem {...itemProps} />
          </Stack>
          <Divider />
          {/* Column management */}
          <Stack px={0.5} py={0.5}>
            <GridColumnMenuColumnsItem {...itemProps} />
            <MenuItem
              onClick={(event) => {
                setColumnOrderDialogOpen(true);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <Reorder fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update Position</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={(event) => {
                deleteHeader((colDef as any).headerId);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Action</ListItemText>
            </MenuItem>
          </Stack>
        </React.Fragment>
      );
    } else {
      // Regular column menu
      return (
        <React.Fragment>
          {/* Sorting and filtering */}
          <Stack px={0.5} py={0.5}>
            <GridColumnMenuSortItem {...itemProps} />
            <GridColumnMenuFilterItem {...itemProps} />
          </Stack>
          <Divider />
          {/* Column management */}
          <Stack px={0.5} py={0.5}>
            <GridColumnMenuColumnsItem {...itemProps} />
            <MenuItem
              onClick={(event) => {
                editHeaderType((colDef as any).header);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <SettingsApplications fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Type</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={(event) => {
                setColumnOrderDialogOpen(true);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <Reorder fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update Position</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={(event) => {
                deleteHeader((colDef as any).headerId);
                hideMenu(event);
              }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete Column</ListItemText>
            </MenuItem>
          </Stack>
        </React.Fragment>
      );
    }
  }

  const editHeaderType = (header: any) => {
    setEditHeaderOpen(true);
    setSelectedHeader(header);
  };

  const reorderColumns = async (orderedHeaderIds: string[]) => {
    const currentToken = await getToken();
    try {
      await axios.post(
        `${hostname}/lists/${instanceId}/headers/reorder`,
        { companyId, orderedHeaderIds },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      getList();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const deleteHeader = async (headerId: string) => {
    const currentToken = await getToken();
    openDialog(
      "Delete Column",
      `Are you sure you want to delete this column and its data?`,
      "Confirm",
      () => {
        axios
          .delete(`${hostname}/lists/${instanceId}/header/${headerId}?companyId=${companyId}`, {
            headers: { Authorization: `Bearer ${currentToken}` },
          })
          .finally(() => {
            getList();
          });
      }
    );
  };

  const getList = async (): Promise<boolean> => {
    const currentToken = await getToken();
    setLoading(true);

    // Get current sort model
    const sortField = sortModel.length > 0 ? sortModel[0].field : undefined;
    const sortOrder = sortModel.length > 0 ? sortModel[0].sort : undefined;

    // Build params including filterModel if present
    const params: Record<string, any> = {
      companyId,
      page: paginationRef.current.page + 1,
      pageSize: paginationRef.current.pageSize,
      sortField,
      sortOrder,
      searchQuery,
      searchField,
    };

    // Include filter model for server-side filtering
    if (filterModel.items && filterModel.items.length > 0) {
      params.filterModel = JSON.stringify(filterModel);
    }

    await axios
      .get(`${hostname}/lists/${instanceId}`, {
        params,
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setName(res.data.list.name);
        setRawHeaders(res.data.list.headers);
        setRows(res.data.list.rows.items);
        setTotalRows(res.data.list.rows.total);
        setActions(res.data.list.actions);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
    return true;
  };

  const processRowUpdate = async (updatedRow: any) => {
    await saveRow(updatedRow);
    return updatedRow;
  };

  const handleProcessRowUpdateError = async (event: any) => {
    console.log("handle error", event);
  };

  const createListTemplate = async (name: string) => {
    const currentToken = await getToken();
    await axios.post(
      `${hostname}/lists/${instanceId}/templates`,
      { companyId, name },
      {
        headers: { Authorization: `Bearer ${currentToken}` },
      }
    );
  };

  const deleteSelected = async () => {
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/lists/${instanceId}/delete`,
        { companyId, rowIds: rowSelectionModel },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    await getList();
  };

  const handleBack = () => {
    setName("");
    navigate(-1);
  };

  // Clear state when instanceId changes to prevent showing old data
  useEffect(() => {
    if (instanceId) {
      setLoading(true);
      setName("Loading...");
      setRawHeaders([]);
      setRows([]);
      setActions([]);
      setTotalRows(0);
      // Reset pagination to first page for new list
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      setSortModel([]);
    }
  }, [instanceId]);

  useEffect(() => {
    paginationRef.current = paginationModel;
    if (instanceId && !isSearchTriggeredRef.current) {
      getList();
    }
    // Reset the flag after handling
    isSearchTriggeredRef.current = false;
  }, [paginationModel, sortModel, filterModel, instanceId]);

  useEffect(() => {
    if (instanceId && (searchQuery || searchField !== "all")) {
      // Debounce the search to avoid too many requests
      const timeoutId = setTimeout(() => {
        isSearchTriggeredRef.current = true; // Set flag to prevent duplicate request
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize }); // Reset to first page when searching
        getList();
      }, 500); // 500ms delay

      // Cleanup function to cancel the timeout if dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, searchField]);

  // Check for Apollo integration availability
  useEffect(() => {
    const checkApolloIntegration = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/apollo`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { companyId },
          }
        );
        // API returns 'items' not 'integrations'
        setHasApolloIntegration(
          response.data.items && response.data.items.length > 0
        );
      } catch {
        setHasApolloIntegration(false);
      }
    };
    if (companyId) {
      checkApolloIntegration();
    }
  }, [companyId, getToken]);

  if (!instanceId) {
    return <></>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        <IconButton
          sx={{ mr: 2 }}
          onClick={handleBack}
          color="primary"
          size={"large"}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{ mr: 1, fontFamily: '"Cairo", sans-serif' }}
        >
          {name ? name : "Loading..."}
        </Typography>
      </Box>
      {/* Search bar - separate line */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <TextField
            placeholder="Search records..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Field</InputLabel>
            <Select
              value={searchField}
              label="Field"
              onChange={(e) => setSearchField(e.target.value)}
            >
              <MenuItem value="all">All Fields</MenuItem>
              {dataColumns
                .filter(
                  (col: any) => col.field !== "id" && col.field !== "__check__"
                )
                .map((col: any) => (
                  <MenuItem key={col.field} value={col.field}>
                    {col.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Action buttons - separate line */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { xs: "stretch", sm: "flex-end" },
          mt: 2,
          justifyContent: "flex-end",
        }}
      >
        {/* Primary actions - always visible */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          {/* Mobile: Show primary actions + menu button */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 1,
              flexDirection: "column",
            }}
          >
            {rowSelectionModel.length !== 0 && (
              <Button
                onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
                endIcon={<ArrowDropDown />}
                color="primary"
                variant="outlined"
              >
                Selected ({rowSelectionModel.length})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<ElectricBolt />}
              onClick={(e: any) => {
                setAnchorEl(e.currentTarget);
              }}
            >
              Table Actions
            </Button>
            <Button
              variant="outlined"
              startIcon={<MoreVert />}
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
            >
              More Actions
            </Button>
          </Box>

          {/* Selected menu - shared between mobile and desktop */}
          <Menu
            anchorEl={moveMenuAnchor}
            open={Boolean(moveMenuAnchor)}
            onClose={() => setMoveMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMoveMenuAnchor(null);
                setMoveToSequenceOpen(true);
              }}
            >
              <ListItemIcon>
                <MoveDown fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move to Sequence</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMoveMenuAnchor(null);
                setMoveToListOpen(true);
              }}
            >
              <ListItemIcon>
                <MoveDown fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move to List</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setMoveMenuAnchor(null);
                deleteSelected();
              }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Selected</ListItemText>
            </MenuItem>
          </Menu>

          {/* Desktop: Show all actions */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              "& .MuiButton-root": {
                whiteSpace: "nowrap",
              },
            }}
          >
            {rowSelectionModel.length !== 0 && (
              <Button
                onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
                endIcon={<ArrowDropDown />}
                color="primary"
                variant="outlined"
              >
                Selected ({rowSelectionModel.length})
              </Button>
            )}
            <Button
              onClick={(e) => setImportEl(e.target)}
              startIcon={<UploadFile />}
              variant="outlined"
            >
              Import
            </Button>
            <Button
              startIcon={<CopyAll />}
              onClick={() => setCreateTemplateOpen(true)}
              variant="outlined"
            >
              Save as Template
            </Button>
            <Button
              startIcon={<DeleteSweep />}
              onClick={() => setDeduplicateOpen(true)}
              variant="outlined"
            >
              Deduplicate
            </Button>
            <Button startIcon={<Add />} onClick={addRow} variant="outlined">
              Add Record
            </Button>
            <Button
              startIcon={<Add />}
              onClick={() => setAddColumnOpen(true)}
              variant="outlined"
            >
              Add Column
            </Button>
            <Button
              data-tutorial="list-actions"
              variant="contained"
              onClick={(e: any) => {
                setAnchorEl(e.currentTarget);
              }}
              startIcon={<ElectricBolt />}
            >
              Table Actions
            </Button>
          </Box>
        </Box>

        {/* Import submenu */}
        <Menu
          open={Boolean(importEl)}
          anchorEl={importEl}
          onClose={() => setImportEl(false)}
        >
          <MenuItem
            onClick={() => {
              setImportOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <UploadFileOutlined />
            </ListItemIcon>
            <ListItemText>Import CSV</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setWebhookOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <Webhook />
            </ListItemIcon>
            <ListItemText>Import from Webhook</ListItemText>
          </MenuItem>
          {isFeatureEnabled(FEATURES.AUDIENCE_BUILDER) && (
            <MenuItem
              onClick={() => {
                setPixelDialogOpen(true);
                setImportEl(null);
              }}
            >
              <ListItemIcon>
                <TrackChanges />
              </ListItemIcon>
              <ListItemText>Website Leads Pixel</ListItemText>
            </MenuItem>
          )}
          {/* Original people search - commented out until fixed
          <MenuItem
            disabled
            onClick={() => {
              setPeopleSearchOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <PersonSearch />
            </ListItemIcon>
            <ListItemText
              primary="Search People"
              secondary="(Currently under maintenance)"
            />
          </MenuItem>
          */}
          <MenuItem
            onClick={() => {
              setApifyLeadsFinderOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <PersonSearch />
            </ListItemIcon>
            <ListItemText>People Search</ListItemText>
          </MenuItem>
          <Tooltip
            title={
              hasApolloIntegration
                ? ""
                : "Connect your Apollo account in the Integrations menu to use Apollo Search"
            }
            placement="right"
          >
            <span>
              <MenuItem
                disabled={!hasApolloIntegration}
                onClick={() => {
                  setApolloSearchOpen(true);
                  setImportEl(null);
                }}
              >
                <ListItemIcon>
                  <ApolloIcon />
                </ListItemIcon>
                <ListItemText>Apollo People Search</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {isFeatureEnabled(FEATURES.AUDIENCE_BUILDER) && (
            <MenuItem
              onClick={() => {
                setAudienceLabDialogOpen(true);
                setImportEl(null);
              }}
            >
              <ListItemIcon>
                <AudienceLabIcon />
              </ListItemIcon>
              <ListItemText>Audience Builder</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setLinkedInPostImportOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <LinkedIn />
            </ListItemIcon>
            <ListItemText>Import LinkedIn Post</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setGoogleMapsSearchOpen(true);
              setImportEl(null);
            }}
          >
            <ListItemIcon>
              <Place />
            </ListItemIcon>
            <ListItemText>Google Maps Search</ListItemText>
          </MenuItem>
          {/* <MenuItem onClick={() => setCompaniesSearchOpen(true)}>
            <ListItemIcon>
              <Business />
            </ListItemIcon>
            <ListItemText>Search Companies</ListItemText>
          </MenuItem> */}
        </Menu>

        {/* Mobile menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => setMobileMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              getList();
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Refresh />
            </ListItemIcon>
            <ListItemText>Refresh Data</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              addRow();
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Add />
            </ListItemIcon>
            <ListItemText>Add Record</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setImportEl(mobileMenuAnchor);
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <UploadFile />
            </ListItemIcon>
            <ListItemText>Import</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setCreateTemplateOpen(true);
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <CopyAll />
            </ListItemIcon>
            <ListItemText>Save as Template</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDeduplicateOpen(true);
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <DeleteSweep />
            </ListItemIcon>
            <ListItemText>Deduplicate</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddColumnOpen(true);
              setMobileMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Add />
            </ListItemIcon>
            <ListItemText>Add Column</ListItemText>
          </MenuItem>
        </Menu>

        {/* Table Actions menu */}
        <Menu
          anchorEl={anchorEl}
          open={anchorEl !== null}
          onClose={() => setAnchorEl(null)}
        >
          {availableListActions.map((action, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                setAnchorEl(null);
                if (action.lookupType) {
                  addLookupColumn(action.lookupType);
                  return;
                }
                setListActionTitle(action.title);
                setListActionType(action.type);
                setActionOptions(action.options);
                setResearchOpen(true);
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <ListItemIcon>
                <ActionIcon action={action} />
              </ListItemIcon>
              <ListItemText>{action.title}</ListItemText>
              {action.description && (
                <Tooltip title={action.description} placement="right" arrow>
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              )}
            </MenuItem>
          ))}
          <Divider sx={{ display: actions.length === 0 ? "none" : "" }} />
          {actions.map((action, index) => (
            <MenuItem
              key={index}
              onClick={(event) => {
                runTableAction(action.id, "add_rows");
                setAnchorEl(null);
              }}
            >
              Run: {action.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Background Tasks Panel */}
      <BackgroundTasksPanel
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        refreshTrigger={backgroundTaskRefreshTrigger}
        onTaskComplete={() => {
          // Refresh the list data when a task completes
          console.log("Background task completed, refreshing list...");
          silentGetList();
        }}
      />

      <Box sx={{ mt: 2 }}>
        <Paper
          variant="outlined"
          sx={{ height: "calc(100vh - 220px)", minHeight: 600, width: "100%" }}
        >
          <StyledDataGrid
            loading={loading}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.rowId}
            pageSizeOptions={[25, 50, 100]}
            rowCount={totalRows}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={(e) => setPaginationModel(e)}
            sortModel={sortModel}
            sortingMode="server"
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterModel={filterModel}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            slots={{
              toolbar: CustomToolbar,
              columnMenu: CustomColumnMenu,
            }}
            onCellEditStop={(params: any, event: any, details: any) => {
              console.log("cell edit", params, details);
            }}
            isCellEditable={(params: any) => {
              // Disable inline editing for cells with long text (>50 chars)
              // These will use the PopOutTextDialog instead
              const value = params.value;
              if (typeof value === "string" && value.length > 50) {
                return false;
              }
              return true;
            }}
            processRowUpdate={(updatedRow: any, originalRow: any) =>
              processRowUpdate(updatedRow)
            }
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            onProcessRowUpdateError={handleProcessRowUpdateError}
          />
        </Paper>
        <ActionTable
          actions={actions}
          onRunAction={(action) => runTableAction(action.id, "add_rows")}
          onEditAction={(action) => {
            setResearchOpen(true);
            console.log("action", action);
            setActionOptions({
              actionId: action.id,
              actionType: action.type,
              autoRunEnabled: action.autoRunEnabled,
              conditions: action.conditions,
              actionParameters: {
                action: "add_rows",
                name: action.name,
                ...action.actionParameters,
              },
            });
            const actionType = action.actionParameters.actionType || "agent";
            const listAction = LIST_ACTIONS.find((x) => x.type === actionType);
            setListActionType(listAction?.type ?? LIST_ACTIONS[0].type);
            setListActionTitle(listAction?.title ?? LIST_ACTIONS[0].title);
          }}
          onStopAction={(action: any) => {
            stopTableAction(action.id, action.type);
          }}
          onDeleteAction={deleteTableAction}
          onViewSummary={(action: any) => {
            setSelectedActionForSummary(action);
            setActionSummaryOpen(true);
          }}
          onViewMetrics={(action: any) => {
            setSelectedActionForMetrics(action);
            setActionMetricsOpen(true);
          }}
        />
        <Menu
          anchorEl={actionAnchor}
          anchorReference={actionAnchorPosition ? "anchorPosition" : "anchorEl"}
          anchorPosition={actionAnchorPosition || undefined}
          open={actionAnchor !== null || actionAnchorPosition !== null}
          onClose={() => {
            setActionAnchor(null);
            setActionAnchorPosition(null);
            setSelectedActionId(null);
          }}
        >
          <MenuItem
            onClick={() => {
              runTableAction(selectedActionId, "update_rows");
              setActionAnchor(null);
              setActionAnchorPosition(null);
              setSelectedActionId(null);
            }}
          >
            Run all rows that haven't run or have errors
          </MenuItem>
          <MenuItem
            onClick={() => {
              runTableAction(selectedActionId, "update_rows", undefined, true);
              setActionAnchor(null);
              setActionAnchorPosition(null);
              setSelectedActionId(null);
            }}
            color={"danger"}
          >
            Force run all rows
          </MenuItem>
          {rowSelectionModel.length !== 0 && (
            <MenuItem
              onClick={() => {
                runTableAction(
                  selectedActionId,
                  "update_rows",
                  rowSelectionModel
                );
                setActionAnchor(null);
                setActionAnchorPosition(null);
                setSelectedActionId(null);
              }}
            >
              Run on selected rows ({rowSelectionModel.length})
            </MenuItem>
          )}
        </Menu>
      </Box>
      <ImportModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          originalPoll(
            silentGetList,
            () => console.log("completed polling"),
            100
          );
        }}
        getToken={getToken}
        eventListener={eventListener}
        listId={instanceId}
        companyId={companyId}
        onBackgroundTaskCreated={() => {
          // Trigger background tasks panel refresh
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
      />
      <ListActionDialog
        open={researchOpen}
        onClose={() => {
          setResearchOpen(false);
          getList();
        }}
        title={listActionTitle}
        type={listActionType}
        headers={dataColumns}
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        currentPlan={currentPlan}
        options={actionOptions}
      />
      <AddColumnDialog
        open={addColumnOpen}
        onClose={() => setAddColumnOpen(false)}
        onSave={addColumn}
        loading={addingColumn}
      />
      <WebhookDialog
        open={webhookOpen}
        onClose={() => setWebhookOpen(false)}
        listId={instanceId}
      />
      <TemplateEditor
        open={createTemplateOpen}
        onClose={() => setCreateTemplateOpen(false)}
        onConfirm={createListTemplate}
      />
      <ListColumnTypeDialog
        open={editHeaderOpen}
        onClose={() => {
          setEditHeaderOpen(false);
          setSelectedHeader(null);
          silentGetList();
        }}
        selectedHeader={selectedHeader}
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
      <RowDetailDialog
        open={rowDetailOpen}
        onClose={() => {
          setRowDetailOpen(false);
          setRowDetailRow(null);
        }}
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        row={rowDetailRow}
        headers={rawHeaders}
        onRowSaved={(updatedRow: any) => {
          setRows((prev: any[]) =>
            prev.map((r) => (r.rowId === updatedRow.rowId ? updatedRow : r))
          );
        }}
      />
      <ListAgentRunSummaryDialog
        open={agentRunSummaryOpen}
        onClose={() => {
          setAgentRunSummaryOpen(false);
          setSelectedHeader(null);
          setSelectedRowId(null);
        }}
        selectedHeader={selectedHeader}
        selectedRowId={selectedRowId}
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
      <ListActionSummaryDialog
        open={actionSummaryOpen}
        onClose={() => {
          setActionSummaryOpen(false);
          setSelectedActionForSummary(null);
        }}
        selectedAction={selectedActionForSummary}
        listId={instanceId}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
      <ActionMetricsDialog
        open={actionMetricsOpen}
        onClose={() => {
          setActionMetricsOpen(false);
          setSelectedActionForMetrics(null);
        }}
        listId={instanceId}
        actionId={selectedActionForMetrics?.id || ""}
        actionType={selectedActionForMetrics?.type || ""}
        actionName={selectedActionForMetrics?.name || ""}
        companyId={companyId}
        getToken={getToken}
      />
      <CompaniesSearchDialog
        open={companiesSearchOpen}
        onClose={() => setCompaniesSearchOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
      />
      <LinkedInPostImportDialog
        open={linkedInPostImportOpen}
        onClose={() => setLinkedInPostImportOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
      />
      <ApifyLeadsFinderDialog
        open={apifyLeadsFinderOpen}
        onClose={() => setApifyLeadsFinderOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
        onBackgroundTaskCreated={() => {
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
      />
      <ApolloSearchDialog
        open={apolloSearchOpen}
        onClose={() => setApolloSearchOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
        onBackgroundTaskCreated={() => {
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
      />
      <GoogleMapsSearchDialog
        open={googleMapsSearchOpen}
        onClose={() => setGoogleMapsSearchOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
        onBackgroundTaskCreated={() => {
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
      />
      <AudienceBuilderDialog
        open={audienceLabDialogOpen}
        onClose={() => setAudienceLabDialogOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        listName={name}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
        onBackgroundTaskCreated={() => {
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
        openDialog={openDialog}
      />
      <MoveToSequenceDialog
        open={moveToSequenceOpen}
        onClose={() => setMoveToSequenceOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        selectedRowIds={rowSelectionModel as readonly string[]}
        onSuccess={() => {
          setRowSelectionModel([]);
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
      />
      <MoveToListDialog
        open={moveToListOpen}
        onClose={() => setMoveToListOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        selectedRowIds={rowSelectionModel as readonly string[]}
        onSuccess={() => {
          setRowSelectionModel([]);
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
      />
      <DeduplicateDialog
        open={deduplicateOpen}
        onClose={() => setDeduplicateOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        companyId={companyId}
        listId={instanceId}
        columns={dataColumns}
        onSuccess={() => {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          getList();
        }}
        onBackgroundTaskCreated={() => {
          // Trigger background tasks panel refresh
          setBackgroundTaskRefreshTrigger((prev) => prev + 1);
        }}
      />
      <PixelDialog
        open={pixelDialogOpen}
        onClose={() => setPixelDialogOpen(false)}
        listId={instanceId}
        listName={name}
        companyId={companyId}
        getToken={getToken}
      />
      <ColumnOrderDialog
        open={columnOrderDialogOpen}
        onClose={() => setColumnOrderDialogOpen(false)}
        columns={dataColumns
          .filter((col: any) => col.headerId)
          .map((col: any) => ({
            headerId: col.headerId,
            name: col.name || col.field,
            type: col.type,
          }))}
        onSave={reorderColumns}
      />
      <SavedViewDialog
        open={savedViewDialogOpen}
        onClose={() => setSavedViewDialogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        currentFilterModel={filterModel}
        currentSortModel={sortModel}
        currentColumnVisibilityModel={columnVisibilityModel}
        onApplyView={handleApplySavedView}
      />
    </Box>
  );
};
